import {
  useCallback, useEffect, useState,
} from 'react';
import { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import type { LatLng } from '~/_shared/types/latLng';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import { createRadiusFilterItemRequest } from '~/spreadsheet/filter/radius/spreadsheetFilterRadius.factory';
import {
  type DemographicsQueryBulkResults, getDemographicsQueryBulk,
} from '~/store/demographics/demographics.repository';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';

export const useExportReportData = (template: ReportTemplate | null, center: LatLng | null) => {
  const [data, setData] = useState<DemographicsQueryBulkResults | null>(null);
  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector();

  const getDemographicValue = useCallback((columnId: string, demographicId: string) => {
    if (!data || !data[columnId]) {
      return null;
    }

    return data[columnId].data.find(demographic => demographic.demographicId === demographicId)?.value.toString() ?? null;
  }, [data]);

  const getDemographicLabel = useCallback((demographicId: string) => {
    if (!data) {
      return null;
    }

    for (const item of Object.values(data)) {
      if (!item.isSuccess) {
        continue;
      }

      for (const demographic of item.data) {
        if (demographic.demographicId === demographicId) {
          return demographic.name;
        }
      }
    }

    return null;
  }, [data]);

  useEffect(() => {
    if (!template || !center || !clientId || !mapId) {
      return;
    }

    const demographicIds: string[] = template.items.reduce<string[]>((acc, item) => {
      const newItems: string[] = [];
      if (item.type !== 'table') {
        return acc;
      }

      item.content.forEach(row => {
        if (row.type === 'data-row') {
          newItems.push(row.id);
        }
      });

      return [...acc, ...newItems];
    }, []);

    getDemographicsQueryBulk(clientId, {
      queries: [{
        id: 'radius5',
        map_id: mapId,
        demographics_ids: demographicIds,
        radius_filter: {
          type: 'or',
          radiuses: [createRadiusFilterItemRequest({
            radius: 5000,
            center,
            unit: UnitSystem.metric,
          })],
        },
      }, {
        id: 'radius10',
        map_id: mapId,
        demographics_ids: demographicIds,
        radius_filter: {
          type: 'or',
          radiuses: [createRadiusFilterItemRequest({
            radius: 10000,
            center,
            unit: UnitSystem.metric,
          })],
        },
      }, {
        id: 'radius15',
        map_id: mapId,
        demographics_ids: demographicIds,
        radius_filter: {
          type: 'or',
          radiuses: [createRadiusFilterItemRequest({
            radius: 15000,
            center,
            unit: UnitSystem.metric,
          })],
        },
      }],
    })
      .then(results => {
        setData(results);
      });
  }, [center, template, clientId, mapId]);

  useEffect(() => {
    if (!data) {
      return;
    }

    Object.assign(window, {
      isReportFullyLoaded: true,
    });
  }, [data]);

  return {
    getDemographicValue,
    getDemographicLabel,
  };
};
